import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Check, X, LinkIcon, Edit2 } from 'lucide-react';
import { useClueSuggestions } from '@/hooks/useClueSuggestions';
import { toast } from '@/hooks/use-toast';

export function ClueSuggestionsAdmin() {
  const { 
    loading, 
    pendingSuggestions, 
    fetchPendingSuggestions, 
    reviewSuggestion 
  } = useClueSuggestions();
  
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedClue, setEditedClue] = useState('');

  useEffect(() => {
    fetchPendingSuggestions();
  }, []);

  const handleApprove = async (id: number) => {
    const clue = editingId === id ? editedClue : undefined;
    const result = await reviewSuggestion(id, 'approve', clue);
    if (result.error) {
      toast({ title: 'Fel', description: result.error, variant: 'destructive' });
    } else {
      toast({ title: 'Godkänt', description: 'Ledtråden har lagts till.' });
      setEditingId(null);
    }
  };

  const handleReject = async (id: number) => {
    const result = await reviewSuggestion(id, 'reject');
    if (result.error) {
      toast({ title: 'Fel', description: result.error, variant: 'destructive' });
    } else {
      toast({ title: 'Avvisat', description: 'Förslaget har avvisats.' });
    }
  };

  const startEditing = (id: number, currentClue: string) => {
    setEditingId(id);
    setEditedClue(currentClue);
  };

  if (loading && pendingSuggestions.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (pendingSuggestions.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Inga ledtrådsförslag att granska.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <LinkIcon className="w-4 h-4" />
        <span>{pendingSuggestions.length} ledtrådsförslag att granska</span>
      </div>
      
      {pendingSuggestions.map((suggestion) => {
        const wordExists = suggestion.word_exists !== false;
        return (
        <Card key={suggestion.id}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-medium">
                  {suggestion.word}
                </CardTitle>
                {!wordExists && (
                  <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">
                    Nytt ord
                  </Badge>
                )}
              </div>
              <Badge variant="secondary" className="text-xs">
                {suggestion.submitted_by}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {editingId === suggestion.id ? (
              <div className="flex gap-2">
                <Input
                  value={editedClue}
                  onChange={(e) => setEditedClue(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => setEditingId(null)}
                >
                  Avbryt
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-sm flex-1">{suggestion.clue}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => startEditing(suggestion.id, suggestion.clue)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handleApprove(suggestion.id)}
                disabled={loading}
                className="flex-1"
              >
                <Check className="w-4 h-4 mr-1" />
                Godkänn
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleReject(suggestion.id)}
                disabled={loading}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-1" />
                Avvisa
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground">
              {new Date(suggestion.created_at).toLocaleDateString('sv-SE')}
            </p>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
}
